import { cn } from '@today/ui-primitives'
import { isBoundaryColumn } from './ruler-model'

/**
 * Props for {@link FixedWidthRuler}.
 *
 * @property lines - The sample's first decoded lines, shown under the ruler.
 * @property lineLength - The longest line, in code points; the ruler's width.
 * @property boundaries - The columns that start a field (2…lineLength).
 * @property onToggle - Called with a column to add or remove its boundary.
 */
export interface FixedWidthRulerProps {
	lines: readonly string[]
	lineLength: number
	boundaries: readonly number[]
	onToggle: (column: number) => void
}

/**
 * The fixed-width ruler (TO-400 BR-13): one toggle per column 2…lineLength; a
 * pressed column starts a field there. The sample lines sit underneath in a
 * monospace grid so a boundary lines up with the characters it splits. Pure.
 */
export function FixedWidthRuler({ lines, lineLength, boundaries, onToggle }: FixedWidthRulerProps) {
	const columns = Array.from({ length: lineLength }, (_, i) => i + 1)
	const starts = new Set([1, ...boundaries])
	return (
		<div className="flex flex-col gap-1 overflow-x-auto font-mono text-ds-sm text-ds-text">
			<div role="toolbar" aria-label="Field boundaries" className="flex">
				{columns.map((column) =>
					isBoundaryColumn(column, lineLength) ? (
						<button
							key={column}
							type="button"
							aria-label={`Column ${column}`}
							aria-pressed={boundaries.includes(column)}
							onClick={() => onToggle(column)}
							className={cn(
								'w-[1ch] shrink-0 border-l text-center',
								boundaries.includes(column) ? 'border-ds-primary bg-ds-primary text-ds-on-primary' : 'border-ds-border',
							)}
						>
							{column % 10 === 0 ? '|' : '·'}
						</button>
					) : (
						<span key={column} aria-hidden="true" className="w-[1ch] shrink-0 text-center">
							1
						</span>
					),
				)}
			</div>
			<div aria-hidden="true" className="whitespace-pre text-ds-primary">
				{columns.map((c) => (starts.has(c) && c > 1 ? '|' : ' ')).join('')}
			</div>
			{lines.map((line, row) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: sample lines may repeat; their order is their identity
				<div key={row} className="whitespace-pre">
					{line}
				</div>
			))}
		</div>
	)
}
